/**
 * #verify section: how integrity is proven later, with a two-path diagram.
 *
 * Path A (navy): unchanged file -> same fingerprint -> INTEGRITY VERIFIED.
 * Path B (red):  modified file  -> different fingerprint -> FILE HAS CHANGED
 *                SINCE FINALISATION.
 *
 * Status is never colour-only: each terminal carries an icon and a text label.
 * Mobile: the two paths stack; from md they sit side by side. Each path is a
 * vertical node list so nothing needs horizontal scrolling at 320px.
 */
import { Icon, type IconName } from "./icons";
import { VERIFY_POINTS } from "./content";
import { Eyebrow } from "./Sections";

type Node = { icon: IconName; title: string; detail: string };

const PATH_MATCH: readonly Node[] = [
  { icon: "file", title: "File to check", detail: "The file as you hold it today" },
  { icon: "fingerprint", title: "Recalculate fingerprint", detail: "SHA-256 · 3f9a…c21e" },
  { icon: "chain", title: "Compare with finalised record", detail: "Recorded · 3f9a…c21e" },
];

const PATH_MISMATCH: readonly Node[] = [
  { icon: "file", title: "File to check", detail: "A file edited after finalisation" },
  { icon: "fingerprint", title: "Recalculate fingerprint", detail: "SHA-256 · 8b07…44d1" },
  { icon: "chain", title: "Compare with finalised record", detail: "Recorded · 3f9a…c21e" },
];

export function VerificationSection() {
  return (
    <section id="verify" aria-labelledby="verify-title" className="scroll-mt-20 bg-white py-14 sm:py-20 lg:py-24">
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-10 px-4 sm:px-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-start lg:gap-14">
        {/* Copy column */}
        <div>
          <Eyebrow>Verification</Eyebrow>
          <h2 id="verify-title" className="text-[1.75rem] leading-[1.15] sm:text-[2.125rem] lg:text-[2.5rem]">
            How integrity is proven later
          </h2>
          <p className="mt-4 text-base leading-relaxed text-ink-soft sm:text-lg">
            Verification does not rely on trusting a screenshot or a stored copy. It repeats the
            mathematics and compares the result with what was finalised.
          </p>

          <ul className="mt-8 space-y-4">
            {VERIFY_POINTS.map((p) => (
              <li key={p.label} className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-aigentico-navy/[0.06] text-aigentico-navy">
                  <Icon name={p.icon} size={18} />
                </span>
                <span className="text-sm leading-relaxed text-ink sm:text-base">{p.label}</span>
              </li>
            ))}
          </ul>

          <p className="mt-8 border-l-4 border-aigentico-navy bg-bg-soft px-4 py-4 text-base font-medium leading-relaxed text-aigentico-navy sm:text-lg">
            StampmatS helps show what was finalised, when it was finalised, and whether it has changed
            since.
          </p>
          <p className="mt-4 text-sm leading-relaxed text-ink-soft">
            StampmatS proves file integrity and timing. It does not verify whether the content of a
            photo, video, or document is accurate.
          </p>
        </div>

        {/* Diagram column */}
        <figure
          className="rounded-xl border border-border bg-bg-soft p-4 sm:p-6"
          aria-label="Verification outcomes: an unchanged file is verified, a changed file is detected"
        >
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-5">
            <VerifyPath
              tone="navy"
              heading="Unchanged file"
              nodes={PATH_MATCH}
              resultIcon="check"
              result="Integrity verified"
              resultDetail="Fingerprints match the finalised record"
            />
            <VerifyPath
              tone="red"
              heading="Modified file"
              nodes={PATH_MISMATCH}
              resultIcon="alert"
              result="File has changed since finalisation"
              resultDetail="Fingerprints no longer match"
            />
          </div>
          <figcaption className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-ink-soft">
            <Icon name="clock" size={16} className="mt-0.5 shrink-0 text-aigentico-navy" />
            <span>
              In both cases the Bitcoin-anchored proof still shows by when the original finalised record
              existed.
            </span>
          </figcaption>
        </figure>
      </div>
    </section>
  );
}

function VerifyPath({
  tone,
  heading,
  nodes,
  resultIcon,
  result,
  resultDetail,
}: {
  tone: "navy" | "red";
  heading: string;
  nodes: readonly Node[];
  resultIcon: IconName;
  result: string;
  resultDetail: string;
}) {
  const accent = tone === "navy" ? "text-aigentico-navy" : "text-aigentico-red";
  const spine = tone === "navy" ? "bg-aigentico-navy/40" : "bg-aigentico-red/40";
  const terminal =
    tone === "navy"
      ? "border-aigentico-navy bg-aigentico-navy text-white"
      : "border-aigentico-red bg-aigentico-red text-white";

  return (
    <div className="flex flex-col">
      <p className={`mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] ${accent}`}>{heading}</p>
      <ol className="flex flex-1 flex-col">
        {nodes.map((n) => (
          <li key={n.title} className="flex gap-3">
            <div className="flex flex-col items-center" aria-hidden="true">
              <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-white ${accent}`}>
                <Icon name={n.icon} size={18} />
              </span>
              <span className={`my-1 w-px flex-1 ${spine}`} />
            </div>
            <div className="min-w-0 pb-4">
              <p className="text-sm font-medium leading-snug text-ink">{n.title}</p>
              <p className="mt-0.5 font-mono text-[11px] text-ink-soft">{n.detail}</p>
            </div>
          </li>
        ))}
      </ol>
      <div className={`flex items-start gap-3 rounded-md border-2 px-3 py-3 ${terminal}`}>
        <Icon name={resultIcon} size={20} className="mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.12em] leading-snug">{result}</p>
          <p className="mt-0.5 text-[11px] leading-snug opacity-90">{resultDetail}</p>
        </div>
      </div>
    </div>
  );
}
